import moment from 'moment-timezone';

export const isValidDate = (d) => {
  return d instanceof Date && !isNaN(d);
}

export const combineDateAndTime = (date, time) => {
  let combinedDate;
  if (isValidDate(date) && isValidDate(time)) {
    combinedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes()
    );
  } else if (date) {
    combinedDate = date;
  } else if (time) {
    combinedDate = time;
  }

  return combinedDate;
};

export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export const date_sort_asc = function(ob1, ob2, propertyName = null) {
  // This is a comparison function that will result in dates being sorted in
  // ASCENDING order. As you can see, JavaScript's native comparison operators
  // can be used to compare dates. This was news to me.
  let date1;
  let date2;
  if (propertyName) {
    date1 = ob1[propertyName];
    date2 = ob2[propertyName];
  } else {
    date1 = ob1;
    date2 = ob2;
  }
  if (date1 > date2) return 1;
  if (date1 < date2) return -1;
  return 0;
};

export const date_sort_desc = function(ob1, ob2, propertyName = null) {
  // This is a comparison function that will result in dates being sorted in
  // DESCENDING order.
  let date1;
  let date2;
  if (propertyName) {
    date1 = ob1[propertyName];
    date2 = ob2[propertyName];
  } else {
    date1 = ob1;
    date2 = ob2;
  }
  if (date1 > date2) return -1;
  if (date1 < date2) return 1;
  return 0;
};

export const sortDates = (arr, order = 'asc', propertyName) => {
  if (order === 'asc') {
    return arr.sort((ob1, ob2) =>
      date_sort_asc(ob1, ob2, propertyName ?? null)
    );
  } else {
    return arr.sort((ob1, ob2) =>
      date_sort_desc(ob1, ob2, propertyName ?? null)
    );
  }
};

export const dateTimeTimeZoneToUtcFormated = (dateTime, timeZone) => {
  return moment
    .tz(
      formatDate(dateTime),
      timeZone
    )
    .utc().format();
};

export const dateTimeInTimeZone = (dateTime, timeZone) => {
  return moment
    .tz(
      moment(dateTime).format('YYYY-MM-DDTHH:mm'),
      'YYYY-MM-DDTHH:mm',
      timeZone
    )
    .toDate();
};

export const dateWithoutTimeZone = (dateTime, timeZone) => {
  return moment(
    moment.tz(dateTime, timeZone).format('YYYY-MM-DDTHH:mm'),
    'YYYY-MM-DDTHH:mm'
  ).toDate();
};

export const areTwoDateTimesOnTheSameDate = (dateOne, dateTwo) => {
  return (
    dateOne.getUTCFullYear() === dateTwo.getUTCFullYear() &&
    dateOne.getUTCMonth() === dateTwo.getUTCMonth() &&
    dateOne.getUTCDate() === dateTwo.getUTCDate()
  );
};

export const toTimeString = (date) =>
  new Date(date)
    .toLocaleTimeString([], { hour: 'numeric', minute: 'numeric' })
    .replace(':00', '');

export const timeFrame = (fromTime, toTime) =>
  `${toTimeString(fromTime)} - ${toTimeString(toTime)}`;

export const showPreferredTimeWindow = (
  preferredWindow,
  deliveryType,
  settings,
  options = {}
) => {
  const { customPhrase, parenthesized = true } = options;
  let startTime, endTime, dayPeriod, finalTimeFrame;

  if (preferredWindow && preferredWindow.includes(deliveryType)) {
    if (preferredWindow.includes('morning')) {
      startTime = settings?.morningStartTime;
      endTime = settings?.morningEndTime;
      dayPeriod = 'Morning';
    } else if (preferredWindow.includes('afternoon')) {
      startTime = settings?.afternoonStartTime;
      endTime = settings?.afternoonEndTime;
      dayPeriod = 'Afternoon';
    } else if (preferredWindow.includes('evening')) {
      startTime = settings?.eveningStartTime;
      endTime = settings?.eveningEndTime;
      dayPeriod = 'Evening';
    } else if (preferredWindow.includes('no_preference')) {
      dayPeriod = 'No Preference';
    } else if (preferredWindow.includes('specific_time')) {
      // should take from schedule's attributes
      startTime =
        deliveryType === 'delivery'
          ? settings?.start_date_time
          : settings?.end_window_beginning;
      endTime =
        deliveryType === 'delivery'
          ? settings?.start_window_finish
          : settings?.end_date_time;
      dayPeriod = 'Specific Time';
    }
  } else {
    dayPeriod = 'No Preference';
  }

  finalTimeFrame = options.customPhrase?.length
    ? `${options.customPhrase}`
    : 'Preferred Time';
  finalTimeFrame += `: ${dayPeriod} `;
  if (dayPeriod !== 'No Preference') {
    const frame = timeFrame(startTime, endTime);
    finalTimeFrame += options?.parenthesized ? `(${frame})` : frame;
  }

  return finalTimeFrame.trim();
};

export const validateDatesRange = (inTime, outTime) =>
  new Date(outTime).getTime() < new Date(inTime).getTime();
